// What a screen shows before the replica has finished its first full pull (WEB_PLAN.md §3.2, §3.5). Until then an
// empty list means "still loading", not "no stores", so screens must not show their empty state yet. Pure, so the
// wording and the state choice are unit-testable; ScreenStates.tsx renders it.

import type { SyncStatus } from '@/sync/status';

export type FirstSyncState =
  /** The first pull has completed: render the screen (an empty list now really is empty). */
  | { kind: 'ready' }
  /** Still downloading; `applied` rows so far (0 while the first page is in flight). */
  | { kind: 'loading'; applied: number }
  /** The network dropped before the first pull finished; the engine retries on reconnect. */
  | { kind: 'offline'; applied: number }
  /** The server answered with an error; the engine retries on its next poll, Retry pulls now. */
  | { kind: 'error'; applied: number; message: string | null };

export function firstSyncState(status: Pick<SyncStatus, 'initialFetchDone' | 'phase' | 'progress' | 'error'>): FirstSyncState {
  if (status.initialFetchDone) return { kind: 'ready' };
  const applied = Math.max(0, status.progress?.applied ?? 0);
  if (status.phase === 'offline') return { kind: 'offline', applied };
  if (status.phase === 'error') return { kind: 'error', applied, message: status.error };
  return { kind: 'loading', applied };
}

export interface FirstSyncCopy {
  title: string;
  message: string;
  /** Progress line under the bar ("1,240 items so far"); null before the first page lands. */
  detail: string | null;
}

function itemsSoFar(applied: number, locale?: string): string | null {
  if (applied <= 0) return null;
  return `${applied.toLocaleString(locale)} ${applied === 1 ? 'item' : 'items'} so far`;
}

/** The words for each state, in the console's voice (and the sync indicator's "Loading your company data…"). */
export function firstSyncCopy(state: FirstSyncState, locale?: string): FirstSyncCopy {
  switch (state.kind) {
    case 'ready':
      return { title: 'Up to date', message: '', detail: null };
    case 'loading':
      return {
        title: 'Loading your company data…',
        message: 'The first sync downloads everything your role can see. Next time the console opens with it right away.',
        detail: itemsSoFar(state.applied, locale),
      };
    case 'offline':
      return {
        title: "You're offline",
        message: 'Ground Team will finish loading your company data when the connection is back.',
        detail: itemsSoFar(state.applied, locale),
      };
    case 'error':
      return {
        title: "Couldn't load your company data",
        message: state.message
          ? `${state.message.trim().replace(/[.\s]+$/, '')}. Ground Team keeps trying; you can also retry now.`
          : 'Ground Team keeps trying; you can also retry now.',
        detail: itemsSoFar(state.applied, locale),
      };
  }
}
